import clsx from 'clsx';
import * as React from 'react';

import { BLOCK_COLUMN, BLOCK_PARAGRAPH, BLOCK_TABS_ITEM } from '@gitbook/doc-core';
import { hideOnDrag, type PaletteDef, usePaletteTrigger } from '@gitbook/react-uikit';
import { BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps, prettifyShortcut } from '@gitbook/spine-react';
import { useEventCallback, useIsKeyPressed } from '@gitbook/util-react-hooks';

import { shortcuts, useKeyboardShortcuts } from '../../../../Keyboard';
import type { DraggableState } from '../../../DragAndDrop';
import { useBlockPaletteFactory } from '../blockPalette';
import type { BlockLayout } from '../layout';
import { type PositionType, useInsertPaletteFactory } from '../useInsertPalette';

export type BlockControlsProps = {
    isHovered: boolean;
    layout: NonNullable<BlockLayout['controlButtons']>;
    children?: React.ReactNode;
};

export type BlockControlsRef = {
    openCommandPalette: (palette?: PaletteDef) => void;
};

/*
 * BlockControls is a wrapper component that handles the hover state of the
 * controls available for manipulating a block.
 *
 * A block can have an insert button and an options button
 * A block can have an insert button but no options button
 * A block can have an options button but no insert button
 *
 * Each triggers a different palette but the state (visibility)
 * of boths buttons depends on whether either palette is open.
 */
export function BlockControls(props: BlockControlsProps) {
    const { layout, isHovered, children } = props;

    const hasMultiple = Boolean(layout.withInsertButton && layout.withOptionsButton);

    return (
        <div
            contentEditable={false}
            className="absolute z-[5] bg-transparent"
            style={layout.hitArea.style}
        >
            <div
                className={clsx(
                    'relative flex w-full select-none justify-start transition',
                    'data-[multiple=true]:justify-end data-[multiple=true]:gap-1',
                    'opacity-0 has-[[aria-expanded=true]]:opacity-100 data-[hovered=true]:opacity-100',
                    'data-[multiple=true]:[&[data-hovered=false]]:translate-x-1.5'
                )}
                data-multiple={hasMultiple}
                data-hovered={isHovered}
            >
                {children}
            </div>
        </div>
    );
}

interface InsertButtonProps extends Pick<BlockProps, 'editor' | 'node' | 'parent'> {
    withInsertButton: boolean;
    size: ButtonProps['size'];
}

export const InsertButton = React.memo(
    React.forwardRef<BlockControlsRef, InsertButtonProps>(function InsertButton(props, ref) {
        const { editor, parent, node, withInsertButton, size } = props;

        const insertPositionRef = React.useRef<PositionType>('before');
        const isEmptyParagraph = node.type === BLOCK_PARAGRAPH && node.isEmpty;
        const mode = isEmptyParagraph ? 'replace' : 'insert';
        const getPosition = () => (isEmptyParagraph ? 'replace' : insertPositionRef.current);
        const [modals, palette] = useInsertPaletteFactory({
            editor,
            parent,
            node,
            getPosition,
        });
        const paletteTrigger = usePaletteTrigger({ palette });

        const openCommandPalette = useEventCallback(() => paletteTrigger.open());

        React.useImperativeHandle(ref, () => ({ openCommandPalette }), [openCommandPalette]);

        return (
            <>
                {withInsertButton ? (
                    <InsertButtonMemo
                        ref={paletteTrigger.triggerProps.ref}
                        aria-expanded={paletteTrigger.triggerProps['aria-expanded']}
                        onPress={paletteTrigger.open}
                        insertPositionRef={insertPositionRef}
                        mode={mode}
                        size={size}
                    />
                ) : (
                    /*
                            Hidden button used as palette anchor, e.g. 
                            an empty block inside an expandable doesn't show an insert button
                            but a user can trigger the insert palette with `/`. 
                        */
                    <button
                        ref={paletteTrigger.triggerProps.ref}
                        aria-expanded={paletteTrigger.triggerProps['aria-expanded']}
                        type="button"
                        className="size-0"
                    />
                )}
                {modals}
            </>
        );
    })
);

const InsertButtonMemo = React.memo(
    React.forwardRef(function InsertButton(
        props: Pick<ButtonProps, 'size' | 'onPress'> & {
            mode: 'replace' | 'insert';
            insertPositionRef: React.MutableRefObject<PositionType>;
        },
        ref: React.ForwardedRef<HTMLButtonElement>
    ) {
        const { mode, size, onPress, insertPositionRef } = props;
        return (
            <Button
                ref={ref}
                data-exclude-from-copy
                contentEditable={false}
                kind="blank"
                iconOnly
                leadingIcon={Icon.Plus}
                label="Insert block"
                tooltip={
                    mode === 'replace'
                        ? { label: 'Click to add' }
                        : {
                              label: 'Click to add below',
                              description: `${prettifyShortcut('alt')} + click to add above`,
                          }
                }
                size={size}
                className={hideOnDrag}
                onPress={onPress}
                onPressStart={(event) => {
                    insertPositionRef.current = event.altKey ? 'before' : 'after';
                }}
            />
        );
    })
);

interface OptionsButtonProps extends Pick<BlockProps, 'editor' | 'node' | 'parent'> {
    isDragging: boolean;
    dragListeners: DraggableState['listeners'];
    isFocused: boolean;
    palette: (() => PaletteDef) | undefined;
    size: ButtonProps['size'];
}

export const OptionsButton = React.memo(
    React.forwardRef<HTMLButtonElement, OptionsButtonProps>(function OptionsButton(props, ref) {
        const { dragListeners, editor, isDragging, isFocused, node, palette, parent, size } = props;
        const createBlockPalette = useBlockPaletteFactory({ nodeKey: node.key });

        const paletteTrigger = usePaletteTrigger({
            ref,
            palette: () => {
                const paletteDef = palette?.() ?? createBlockPalette();
                return {
                    ...paletteDef,
                    position: {
                        ...paletteDef.position,
                        preferredPlacement: paletteDef.position?.preferredPlacement ?? 'left',
                    },
                };
            },
        });

        const selectBlock = () => {
            // Select the block when options palette is opened
            // expect when multiple blocks are selected.
            if (!isBlockRangeSelection(editor.value.selection)) {
                // TEMP: selection in tabs items with a single node requires reworking logic in `BlockRange.getBlocks`.
                // We block selection in this edge case in the meantime.
                if (
                    parent.nodes.size === 1 &&
                    parent.type &&
                    [BLOCK_TABS_ITEM, BLOCK_COLUMN].includes(parent.type)
                ) {
                    return;
                }

                editor.change((change) => {
                    change.selectBlock(
                        BlockRange.create({
                            anchorBlock: node.key,
                            focusBlock: node.key,
                            isFocused: true,
                        })
                    );
                });
            }
        };

        useKeyboardShortcuts(
            isFocused
                ? {
                      [shortcuts.EDITOR_MODIFIER_PALETTE]: () => {
                          selectBlock();
                          paletteTrigger.open();
                      },
                  }
                : {},
            'editor-selection-collapsed'
        );

        const handlePress = useEventCallback(() => {
            selectBlock();
            paletteTrigger.triggerProps.onPress();
        });

        return (
            <OptionsButtonMemo
                ref={paletteTrigger.triggerProps.ref}
                aria-expanded={paletteTrigger.triggerProps['aria-expanded']}
                onPress={handlePress}
                isDragging={isDragging}
                size={size}
                {...dragListeners}
            />
        );
    })
);

const OptionsButtonMemo = React.memo(
    React.forwardRef(function OptionsButtonMemo(
        props: Omit<ButtonProps, 'label'> & {
            isDragging: boolean;
        },
        ref: React.ForwardedRef<HTMLButtonElement>
    ) {
        const { isDragging, ...rest } = props;
        const isAltPressed = useIsKeyPressed('Alt');
        return (
            <Button
                ref={ref}
                data-exclude-from-copy
                contentEditable={false}
                kind="blank"
                iconOnly
                label="Block controls"
                leadingIcon={Icon.GrabberVertical}
                tooltip={{
                    label: 'Click for block options',
                    description: isAltPressed ? 'Drag to copy' : 'Drag to move',
                    shortcut: shortcuts.EDITOR_MODIFIER_PALETTE,
                    disabled: isDragging,
                }}
                className={clsx(hideOnDrag, isDragging ? 'cursor-grabbing' : 'cursor-grab')}
                {...rest}
            />
        );
    })
);
